import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import MetricCard from '../components/MetricCard'
import CaseCard from '../components/CaseCard'
import { useCasesSnapshot } from '../context/CasesStateContext'
import { fetchCaseMetrics, fetchCases, fetchTeams, fetchPriorities } from '../api/cases'
import styles from './Cases.module.css'

const PAGE_SIZE = 20

// Identity keys for the restored data's filter context. JSON so team names / search
// text containing spaces can't collide. Module-scoped (pure) so they're stable and
// don't need to be effect dependencies.
const metricsKey = (t, p) => JSON.stringify([t, p])
const listKey = (t, p, i, s) => JSON.stringify([t, p, i, s])

// Issue-filter options are derived from the Top Issues metric card's value: a
// { name: count } map (already sorted desc, top 5), parsed defensively — object or
// JSON string, exactly like the bar card renders it. An absent/errored/empty card
// yields [], which leaves the dropdown disabled.
function parseIssueOptions(value) {
  let obj = value
  if (typeof value === 'string') {
    try { obj = JSON.parse(value) } catch { return [] }
  }
  if (!obj || typeof obj !== 'object') return []
  return Object.entries(obj).map(([name, count]) => ({ name, count: Number(count) || 0 }))
}

// ---------------------------------------------------------------------------
// Cases page
// ---------------------------------------------------------------------------
export default function Cases() {
  const navigate = useNavigate()

  // Route-scoped keep-alive cache (see CasesStateContext). If a snapshot exists we
  // are returning from a case detail: rehydrate everything from it and skip the
  // initial refetch. Captured ONCE at mount — the provider hands us the same ref
  // across remounts, but we only ever restore the value that existed when this
  // mount began; later renders write TO it but never re-read it.
  const snapshotRef = useCasesSnapshot()
  const restoreRef = useRef(snapshotRef?.current ?? null)
  const initial = restoreRef.current
  const hydrated = Boolean(initial)

  const [metrics, setMetrics] = useState(() => initial?.metrics ?? [])
  const [teams, setTeams] = useState(() => initial?.teams ?? [])
  const [priorities, setPriorities] = useState(() => initial?.priorities ?? [])
  const [loadingMetrics, setLoadingMetrics] = useState(!hydrated)

  const [cases, setCases] = useState(() => initial?.cases ?? [])
  // Capped ID/number matches shown above the body during an active search (page-1
  // only); pinnedMore is the count beyond the cap for the "+N more" hint.
  const [pinned, setPinned] = useState(() => initial?.pinned ?? [])
  const [pinnedMore, setPinnedMore] = useState(() => initial?.pinnedMore ?? 0)
  const [loadingList, setLoadingList] = useState(!hydrated)   // initial / filter-change load
  const [loadingMore, setLoadingMore] = useState(false)  // infinite-scroll append
  const [hasMore, setHasMore] = useState(() => (hydrated ? initial.hasMore : true))
  const [error, setError] = useState(null)

  const [search, setSearch] = useState(() => initial?.search ?? '')
  const [debouncedSearch, setDebouncedSearch] = useState(() => initial?.debouncedSearch ?? '')
  const [teamFilter, setTeamFilter] = useState(() => initial?.teamFilter ?? 'all')
  const [priorityFilter, setPriorityFilter] = useState(() => initial?.priorityFilter ?? 'all')
  // Issue (Symptom) filter, sourced from the Top Issues metric card. 'all' = unfiltered.
  const [issueFilter, setIssueFilter] = useState(() => initial?.issueFilter ?? 'all')

  // While a keystroke is still settling, search and its debounced commit diverge —
  // infinite scroll pauses so the sentinel can't fetch a page for the query that's
  // about to be replaced.
  const pendingSearch = search.trim() !== debouncedSearch

  const listRef = useRef(null)
  const sentinelRef = useRef(null)
  // Opaque forward cursor for the next page (null = start / no more). A ref, not
  // state, so loadMore always reads the latest without re-subscribing the observer.
  const cursorRef = useRef(initial?.cursor ?? null)
  // Last search term the debounce actually committed — lets the timer skip a reload
  // when the settled value hasn't changed. Seeded from the restored debounced term so
  // the debounce timer doesn't fire a spurious reload right after rehydration.
  const committedSearchRef = useRef(initial?.debouncedSearch ?? '')

  // When rehydrating, the metrics + list fetch effects still run on mount (React
  // always fires effects on mount). Rather than a one-shot flag — which StrictMode's
  // mount→unmount→mount double-invoke would consume on the first pass and then refetch
  // on the second — we record the filter "key" the restored data corresponds to and
  // skip the fetch while the live filters still match it. That's a pure comparison, so
  // both StrictMode passes skip identically. The moment the user changes a filter the
  // key diverges (and we null it, so returning to the same filters still refetches,
  // since the restored data is no longer in state).
  const hydratedMetricsKey = useRef(hydrated ? metricsKey(initial.teamFilter, initial.priorityFilter) : null)
  const hydratedListKey = useRef(hydrated ? listKey(initial.teamFilter, initial.priorityFilter, initial.issueFilter, initial.debouncedSearch) : null)

  // ── One-time load: filter options (StrictMode-guarded, like Team) ──
  // The team/priority option lists are static — loaded once. Metrics are NOT here;
  // they depend on the active filters and reload in their own effect below. Seeded
  // true when rehydrating so the restored option lists aren't refetched.
  const didFetchStatic = useRef(hydrated)
  useEffect(() => {
    if (didFetchStatic.current) return
    didFetchStatic.current = true

    // Filter options are supplementary — a failure leaves the dropdown with just
    // its "All …" option rather than breaking the page.
    fetchTeams().then(d => setTeams(Array.isArray(d) ? d : [])).catch(() => {})
    fetchPriorities().then(d => setPriorities(Array.isArray(d) ? d : [])).catch(() => {})
  }, [])

  // ── Keep-alive: restore scroll on mount, mirror state into the snapshot ──────────
  // Restore the saved scroll position before paint. The full `cases` array is
  // hydrated synchronously above, so the list already has its full height here and
  // the scrollTop lands exactly where the user left it (no flash). Runs once.
  useLayoutEffect(() => {
    if (hydrated && listRef.current) {
      listRef.current.scrollTop = initial.scrollTop || 0
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Mirror the persisted slice of state into the shared snapshot on every change, so
  // whenever this page unmounts (opening a case) the provider already holds the
  // latest — no fragile unmount-time DOM reads. scrollTop is written live by the
  // list's onScroll handler and carried forward here so a state change doesn't clobber
  // it.
  useEffect(() => {
    if (!snapshotRef) return
    snapshotRef.current = {
      metrics, teams, priorities,
      cases, pinned, pinnedMore, hasMore,
      cursor: cursorRef.current,
      search, debouncedSearch, teamFilter, priorityFilter, issueFilter,
      scrollTop: snapshotRef.current?.scrollTop ?? initial?.scrollTop ?? 0,
    }
  }, [snapshotRef, initial, metrics, teams, priorities, cases, pinned, pinnedMore,
      hasMore, search, debouncedSearch, teamFilter, priorityFilter, issueFilter])

  // ── Metrics: reload whenever the team/priority filter changes ────────────────
  // The metric cards are aggregates over the team/priority-scoped set, so they track
  // those two dropdowns (not search — the metrics endpoint has no search dimension).
  // `active`-guarded like the list effect so a stale response can't overwrite a newer
  // one. A failure surfaces in the page error banner.
  // loadingMetrics is flipped by the changeTeam/changePriority handlers (and starts
  // true on mount), not here — setting it synchronously in the effect body triggers a
  // cascading render, matching how the list reload keeps its loading flag in beginReload.
  useEffect(() => {
    // Rehydrated and these filters still match the restored metrics — nothing to fetch.
    if (hydratedMetricsKey.current === metricsKey(teamFilter, priorityFilter)) return
    hydratedMetricsKey.current = null   // filters diverged; never skip again
    let active = true
    const filterTeam = teamFilter === 'all' ? undefined : teamFilter
    const filterPriority = priorityFilter === 'all' ? undefined : priorityFilter
    fetchCaseMetrics({ filterTeam, filterPriority })
      .then(data => active && setMetrics(Array.isArray(data) ? data : []))
      .catch(e => active && setError(e.detail || e.message))
      .finally(() => active && setLoadingMetrics(false))
    return () => { active = false }
  }, [teamFilter, priorityFilter])

  // ── Debounce the search box → commit the term 300ms after typing settles ─────
  // The reset (like a filter change) is flipped here in the timer callback, not in
  // the reload effect body — only when the term actually changes, so an unchanged
  // commit doesn't strand a loading state with no fetch to clear it.
  useEffect(() => {
    const id = setTimeout(() => {
      const next = search.trim()
      if (next === committedSearchRef.current) return
      committedSearchRef.current = next
      beginReload()
      setDebouncedSearch(next)
    }, 300)
    return () => clearTimeout(id)
  }, [search])

  // ── Query dimension change → reload the first page ──────────────────────────
  // team, priority, or the debounced search term. Each is a fresh keyset query: the
  // callers (changeTeam/changePriority, and the debounce above) reset the cursor and
  // loading state; this effect only performs the fetch. Search is just another
  // server-side filter, so it composes with keyset pagination (no duplicates)
  // exactly like the dropdowns.
  useEffect(() => {
    // Rehydrated and team/priority/issue/search still match the restored page(s) — skip
    // so the infinite-scroll position (cursor + loaded pages) isn't reset to page 1.
    if (hydratedListKey.current === listKey(teamFilter, priorityFilter, issueFilter, debouncedSearch)) return
    hydratedListKey.current = null   // a query dimension diverged; never skip again
    let active = true
    const filterTeam = teamFilter === 'all' ? undefined : teamFilter
    const filterPriority = priorityFilter === 'all' ? undefined : priorityFilter
    const filterIssue = issueFilter === 'all' ? undefined : issueFilter
    fetchCases({ cursor: null, count: PAGE_SIZE, filterTeam, filterPriority, filterIssue, search: debouncedSearch || undefined })
      .then(res => {
        if (!active) return
        const page = Array.isArray(res.items) ? res.items : []
        setCases(page)
        // pinned block only rides along on the first page of a search; browse returns none.
        setPinned(Array.isArray(res.pinned) ? res.pinned : [])
        setPinnedMore(res.pinned_more || 0)
        cursorRef.current = res.next_cursor ?? null
        setHasMore(Boolean(res.next_cursor))
        if (listRef.current) listRef.current.scrollTop = 0
      })
      .catch(e => active && setError(e.detail || e.message))
      .finally(() => active && setLoadingList(false))
    return () => { active = false }
  }, [teamFilter, priorityFilter, issueFilter, debouncedSearch])

  // ── Infinite scroll: append the next page when the sentinel scrolls into view ─
  const loadMore = useCallback(async () => {
    if (loadingList || loadingMore || !hasMore || pendingSearch) return
    setLoadingMore(true)
    const filterTeam = teamFilter === 'all' ? undefined : teamFilter
    const filterPriority = priorityFilter === 'all' ? undefined : priorityFilter
    const filterIssue = issueFilter === 'all' ? undefined : issueFilter
    try {
      const res = await fetchCases({ cursor: cursorRef.current, count: PAGE_SIZE, filterTeam, filterPriority, filterIssue, search: debouncedSearch || undefined })
      const page = Array.isArray(res.items) ? res.items : []
      setCases(prev => [...prev, ...page])
      cursorRef.current = res.next_cursor ?? null
      setHasMore(Boolean(res.next_cursor))
    } catch (e) {
      setError(e.detail || e.message)
    } finally {
      setLoadingMore(false)
    }
  }, [loadingList, loadingMore, hasMore, pendingSearch, teamFilter, priorityFilter, issueFilter, debouncedSearch])

  // The observer re-subscribes whenever loadMore changes (i.e. when the loaded count
  // or filters change), so it always calls the current version.
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const io = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadMore() },
      { root: listRef.current, rootMargin: '250px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [loadMore])

  // Flip to the loading state before a query-driven reload. Kept in the (event-
  // handler / debounce) callers rather than the fetch effect so state isn't set
  // synchronously inside an effect body.
  function beginReload() {
    setLoadingList(true)
    setHasMore(true)
    setError(null)
    setPinned([])              // clear the previous query's pinned block
    setPinnedMore(0)
    cursorRef.current = null   // drop the previous query's cursor before the reload
  }

  // Team/priority re-scope the metric cards, and with them the Top Issues option set —
  // so a previously-selected issue may no longer be a top issue. Reset it to 'all' so we
  // never keep filtering by an issue that's dropped out of the dropdown (see changeIssue).
  function changeTeam(value) {
    beginReload()
    setLoadingMetrics(true)
    setIssueFilter('all')
    setTeamFilter(value)
  }

  function changePriority(value) {
    beginReload()
    setLoadingMetrics(true)
    setIssueFilter('all')
    setPriorityFilter(value)
  }

  // Issue filter re-scopes only the case list — the metric cards stay put (they're the
  // source of these options, so re-scoping them by the selection would be circular).
  function changeIssue(value) {
    beginReload()
    setIssueFilter(value)
  }

  function openCase(c) {
    navigate(`/cases/${encodeURIComponent(c.number)}`)
  }

  // A case can match both by number (pinned) and by text (body); drop the body copy so
  // it renders once, under the pinned block. Cheap over one page — no memo needed.
  const pinnedNumbers = new Set(pinned.map(c => c.number))
  const bodyCases = cases.filter(c => !pinnedNumbers.has(c.number))

  // Options for the issue dropdown, sourced from the current Top Issues metric card.
  const issueOptions = parseIssueOptions(metrics.find(m => m.name === 'Top Issues')?.value)

  return (
    <div className={styles.page}>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {/* ── Metric cards ─────────────────────────────── */}
      <div className={styles.metricsRow}>
        {loadingMetrics
          ? Array(4).fill(0).map((_, i) => <div key={i} className={styles.metricSkeleton} />)
          : metrics.map(m => (
              <MetricCard
                key={m.name}
                name={m.name}
                value={m.value}
                description={m.description}
                cardtype={m.cardtype}
                icon={m.icon}
                caption={m.caption}
                unit={m.unit}
                barScale={m.bar_scale}
                errorRef={m.error_ref}
                evidenceHeader={m.evidence_header}
                evidence={m.evidence}
                {...(m.cardtype === 'bar' && { barColumns: 2, maxBars: 6, tightBars: true })}
              />
            ))
        }
      </div>

      {/* ── Search + filters ─────────────────────────── */}
      <div className={styles.controls}>
        <div className={styles.searchGroup}>
          <div className={styles.searchWrap}>
            <input
              className={styles.search}
              type="text"
              placeholder="Search cases by ID, Title, or assignee"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && setSearch('')}
            />
            {search && (
              <button type="button" className={styles.clearSearch} onClick={() => setSearch('')}>
                Clear
              </button>
            )}
          </div>
        </div>

        <div className={styles.filters}>
          <select
            className={styles.filterSelect}
            value={teamFilter}
            onChange={e => changeTeam(e.target.value)}
            aria-label="Filter by team"
          >
            <option value="all">All Teams</option>
            {teams.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>

          <select
            className={styles.filterSelect}
            value={priorityFilter}
            onChange={e => changePriority(e.target.value)}
            aria-label="Filter by priority"
          >
            <option value="all">All Priorities</option>
            {priorities.map(p => <option key={p} value={p}>{p}</option>)}
          </select>

          {/* Issue filter — options are the Top Issues symptoms for the active
              team/priority scope; disabled when that card is empty/errored. */}
          <select
            className={styles.filterSelect}
            value={issueFilter}
            onChange={e => changeIssue(e.target.value)}
            disabled={issueOptions.length === 0}
            aria-label="Filter by issue"
            title={issueOptions.length === 0 ? 'No top issues for the current filters' : undefined}
          >
            <option value="all">All Issues</option>
            {issueOptions.map(o => (
              <option key={o.name} value={o.name}>{o.name} ({o.count})</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Case list (independently scrollable, infinite-scroll) ─────────────── */}
      {/* onScroll mirrors the live scroll position into the keep-alive snapshot so a
          return from a case detail lands at the same spot (scrolling fires no render,
          so this can't go through state). */}
      <div
        className={styles.caseList}
        ref={listRef}
        onScroll={e => { if (snapshotRef?.current) snapshotRef.current.scrollTop = e.currentTarget.scrollTop }}
      >
        {loadingList
          ? Array(4).fill(0).map((_, i) => <div key={i} className={styles.caseSkeleton} />)
          : pinned.length === 0 && bodyCases.length === 0
            ? <div className={styles.emptyState}>No cases match your filters.</div>
            : <>
                {pinned.length > 0 && (
                  <>
                    <div className={styles.pinnedHeader}>
                      <span>Matching cases</span>
                      {pinnedMore > 0 && (
                        <span className={styles.pinnedMore}>+{pinnedMore} more — keep typing to refine</span>
                      )}
                    </div>
                    {pinned.map(c => (
                      <CaseCard key={`pin-${c.number}`} caseData={c} onOpen={() => openCase(c)} />
                    ))}
                    {bodyCases.length > 0 && <div className={styles.pinnedDivider} />}
                  </>
                )}
                {bodyCases.map(c => (
                  <CaseCard key={c.number} caseData={c} onOpen={() => openCase(c)} />
                ))}
              </>
        }

        {/* Sentinel drives the infinite scroll; kept mounted so the observer has a
            stable target. */}
        <div ref={sentinelRef} className={styles.sentinel}>
          {loadingMore && <span className={styles.loadingMore}>Loading more…</span>}
        </div>
      </div>

    </div>
  )
}
